import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { baseurl } from "../../config/url";
import { useNavigate } from "react-router-dom";

const safeText = (v) => (v === null || v === undefined || v === "" ? "-" : String(v));

const inr = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n);
};

const dtIN = (d) => {
  if (!d) return "-";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "-";
  return x.toLocaleString("en-IN");
};

const PAGE_SIZE = 10;

// ✅ seller picker (supports different backend shapes without changing API)
const pickSeller = (p) => {
  // common possibilities: seller, sellerId, createdBy, userId
  return (
    p?.seller ||
    p?.sellerId ||
    p?.createdBy ||
    p?.userId ||
    null
  );
};

const pickSellerId = (p) => {
  const s = pickSeller(p);
  if (!s) return null;
  if (typeof s === "string") return s;
  return s?._id || s?.id || null;
};

const pickSellerName = (p) => {
  const s = pickSeller(p);
  if (!s) return "-";
  if (typeof s === "string") return "View Seller";
  return s?.name || s?.fullName || s?.companyName || s?.shopName || "-";
};

const pickSellerEmail = (p) => {
  const s = pickSeller(p);
  if (!s || typeof s === "string") return "-";
  return s?.email || "-";
};

const pickSellerMobile = (p) => {
  const s = pickSeller(p);
  if (!s || typeof s === "string") return "-";
  return s?.mobile || s?.phone || "-";
};

const ApproveProductsPage = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  // ✅ Pagination
  const [page, setPage] = useState(1);

  // ✅ Fetch products (same API endpoint)
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await axios.get(`${baseurl}/api/product`);

        const list = Array.isArray(res.data?.products) ? res.data.products : [];

        // NOTE:
        // Your previous code filtered `approved === true`.
        // Keeping SAME behavior to avoid changing logic unexpectedly.
        const filtered = list.filter((p) => p.approved === true);

        setProducts(filtered);
      } catch (err) {
        setError(err.response?.data?.message || "Error fetching products");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;

    return products.filter((p) => {
      const name = safeText(p?.name).toLowerCase();
      const cat = safeText(p?.category?.name).toLowerCase();
      const price = safeText(p?.price).toLowerCase();
      const sellerName = safeText(pickSellerName(p)).toLowerCase();

      return name.includes(q) || cat.includes(q) || price.includes(q) || sellerName.includes(q);
    });
  }, [products, search]);

  // ✅ Reset to page 1 when search changes
  useEffect(() => setPage(1), [search]);

  // ✅ Pagination computed
  const totalItems = filteredProducts.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const pageItems = filteredProducts.slice(startIndex, startIndex + PAGE_SIZE);

  const from = totalItems === 0 ? 0 : startIndex + 1;
  const to = Math.min(startIndex + PAGE_SIZE, totalItems);

  // ✅ Approve
  const handleApproveProduct = async (productId) => {
    try {
      setActionLoading(true);
      setError("");

      await axios.put(
        `${baseurl}/approve-product/${productId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
        }
      );

      // remove from list
      setProducts((prev) => prev.filter((p) => p._id !== productId));
      setSelectedProduct(null);
      alert("Product approved successfully!");
    } catch (err) {
      console.error("Error approving product:", err);
      alert(err.response?.data?.message || "Error approving product");
    } finally {
      setActionLoading(false);
    }
  };

  const closeDetails = () => setSelectedProduct(null);

  // ✅ ESC closes details
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeDetails();
    };
    if (selectedProduct) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedProduct]);

  // ✅ keep page valid after list changes
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const goSellerDetails = (p) => {
    const sellerId = pickSellerId(p);
    if (!sellerId) {
      alert("Seller information not available for this product.");
      return;
    }
    navigate(`/seller-details/${sellerId}`);
  };

  // UI states
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="text-lg font-semibold text-gray-800">Loading products…</div>
          <div className="text-sm text-gray-500 mt-1">Please wait</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-red-700 max-w-xl w-full">
          <div className="font-bold">Failed to load</div>
          <div className="text-sm mt-1">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Approve Products</h1>
            <p className="text-sm text-gray-600 mt-1">
              Search, open details, check seller, then approve.
            </p>
          </div>

          <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2">
            Total: <b>{products.length}</b> • Filtered: <b>{filteredProducts.length}</b>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-5">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <label className="text-xs text-gray-500">Search</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by product, category, price, seller..."
                className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            <button
              onClick={() => setSearch("")}
              className="mt-5 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Product</th>
                <th className="px-4 py-3 text-left font-semibold">Category</th>
                <th className="px-4 py-3 text-left font-semibold">Seller</th>
                <th className="px-4 py-3 text-left font-semibold">Price</th>
                <th className="px-4 py-3 text-left font-semibold">Stock</th>
                <th className="px-4 py-3 text-right font-semibold">Action</th>
              </tr>
            </thead>

            <tbody>
              {pageItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                    No products found.
                  </td>
                </tr>
              ) : (
                pageItems.map((p) => (
                  <tr
                    key={p._id}
                    className="border-t hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedProduct(p)}
                    title="Click to view details"
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{safeText(p.name)}</div>
                      <div className="text-xs text-gray-500 font-mono">{safeText(p._id)}</div>
                    </td>

                    <td className="px-4 py-3 text-gray-700">{safeText(p?.category?.name)}</td>

                    <td className="px-4 py-3 text-gray-700">{pickSellerName(p)}</td>

                    <td className="px-4 py-3 text-gray-700">
                      ₹{inr(p.price)}{" "}
                      <span className="text-xs text-gray-500">
                        {p.priceUnit ? `(${p.priceUnit})` : ""}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-gray-700">{safeText(p.stock)}</td>

                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setSelectedProduct(p)}
                          className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                        >
                          View
                        </button>
                        <button
                          onClick={() => goSellerDetails(p)}
                          className="px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                        >
                          Seller
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* ✅ Pagination */}
          {totalItems > 0 && (
            <div className="flex items-center justify-between gap-3 flex-wrap p-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing <b>{from}</b> to <b>{to}</b> of <b>{totalItems}</b>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Prev
                </button>

                {(() => {
                  const maxBtns = 7;
                  let start = Math.max(1, page - Math.floor(maxBtns / 2));
                  let end = Math.min(totalPages, start + maxBtns - 1);
                  start = Math.max(1, end - maxBtns + 1);

                  const pages = [];
                  for (let p = start; p <= end; p++) pages.push(p);

                  return pages.map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-3 py-2 rounded-lg border ${
                        p === page
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-blue-600 border-blue-200 hover:bg-blue-50"
                      }`}
                    >
                      {p}
                    </button>
                  ));
                })()}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-500 mt-3">
          Tip: Click a row to open details. Click <b>Seller</b> to open seller profile. Press{" "}
          <b>ESC</b> to close details.
        </p>
      </div>

      {/* ✅ Details Modal (shows seller info + seller details link) */}
      {selectedProduct && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
          onClick={closeDetails}
        >
          <div
            className="bg-white w-full max-w-3xl rounded-2xl shadow-lg border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-5 border-b flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{safeText(selectedProduct.name)}</h2>
                <div className="text-xs text-gray-500 font-mono mt-1 break-all">
                  {safeText(selectedProduct._id)}
                </div>
              </div>

              <button
                onClick={closeDetails}
                className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <InfoCard label="Category" value={selectedProduct?.category?.name || "Uncategorized"} />
                <InfoCard
                  label="Price"
                  value={`₹${inr(selectedProduct.price)} ${
                    selectedProduct.priceUnit ? `(${selectedProduct.priceUnit})` : ""
                  }`}
                />
                <InfoCard label="Stock" value={safeText(selectedProduct.stock)} />
                <InfoCard label="Max Quantity" value={safeText(selectedProduct.maxquantity)} />
                <InfoCard label="Approved" value={String(!!selectedProduct.approved)} />
                <InfoCard label="Created At" value={dtIN(selectedProduct.createdAt)} />
              </div>

              {/* Seller */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs text-gray-500">Seller / Created By</div>
                    <div className="text-base font-semibold text-gray-900 mt-1">
                      {pickSellerName(selectedProduct)}
                    </div>
                    <div className="text-sm text-gray-700 mt-1">
                      Email: <span className="font-semibold">{pickSellerEmail(selectedProduct)}</span>
                    </div>
                    <div className="text-sm text-gray-700">
                      Mobile: <span className="font-semibold">{pickSellerMobile(selectedProduct)}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => goSellerDetails(selectedProduct)}
                    className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    View Seller
                  </button>
                </div>

                <div className="text-xs text-gray-500 mt-2">
                  Seller ID:{" "}
                  <span className="font-mono">{safeText(pickSellerId(selectedProduct))}</span>
                </div>
              </div>

              {/* Description */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="text-xs text-gray-500">Description</div>
                <div className="text-sm text-gray-800 mt-1 leading-relaxed">
                  {selectedProduct.description || "No description available."}
                </div>
              </div>

              {/* Optional fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <InfoCard label="Price Unit" value={safeText(selectedProduct.priceUnit)} />
                <InfoCard label="Updated At" value={dtIN(selectedProduct.updatedAt)} />
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 border-t flex items-center justify-end gap-2 flex-wrap">
              <button
                onClick={closeDetails}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800"
                disabled={actionLoading}
              >
                Close
              </button>

              <button
                onClick={() => handleApproveProduct(selectedProduct._id)}
                disabled={actionLoading}
                className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-60"
              >
                {actionLoading ? "Approving..." : "Approve Product"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoCard = ({ label, value }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-sm font-semibold text-gray-900 mt-1 break-all">
      {safeText(value)}
    </div>
  </div>
);

export default ApproveProductsPage;










































// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { baseurl } from "../../config/url";

// const ApproveProductsPage = () => {
//   const [products, setProducts] = useState([]);
//   const [selectedProduct, setSelectedProduct] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   // Fetch products with approved status false
//   useEffect(() => {
//     const fetchUnapprovedProducts = async () => {
//       try {
//         const response = await axios.get(`${baseurl}/api/product`);
//         console.log("API Response:", response.data);

//         const unapprovedProducts = response.data.products.filter(
//           (product) => product.approved === true
//         );

//         setProducts(unapprovedProducts);
//       } catch (err) {
//         setError(err.response ? err.response.data.message : "Error fetching products");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchUnapprovedProducts();
//   }, []);

//   const handleApproveProduct = async (productId) => {
//     try {
//       const response = await axios.put(
//         `${baseurl}/approve-product/${productId}`,
//         {},
//         {
//           headers: {
//             Authorization: `Bearer ${localStorage.getItem("token")}`,
//           },
//         }
//       );
//       console.log("Approve Response:", response.data);

//       setProducts((prevProducts) =>
//         prevProducts.filter((product) => product._id !== productId)
//       );
//       alert("Product approved successfully!");
//       setSelectedProduct(null); // Close the sidebar
//     } catch (err) {
//       console.error("Error approving product:", err);
//       alert(err.response ? err.response.data.message : "Error approving product");
//     }
//   };

//   const closeSidebar = () => {
//     setSelectedProduct(null);
//   };

//   if (loading) return <div>Loading...</div>;
//   if (error) return <div>Error: {error}</div>;

//   return (
//     <div className="min-h-screen bg-gradient-to-r from-green-50 via-white to-green-100 p-8">
//       <div className="relative flex">
//         {/* Product List */}
//         <div className={`flex-1 bg-white shadow-lg rounded-lg p-6 ${selectedProduct ? "mr-96" : ""}`}>
//           <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">Approve Products</h1>
//           <div className="overflow-x-auto">
//             <table className="table-auto w-full border-collapse border border-gray-300">
//               <thead className="bg-gray-200">
//                 <tr>
//                   <th className="border border-gray-300 px-4 py-2">Product Name</th>
//                   <th className="border border-gray-300 px-4 py-2">Max Quantity</th>
//                   <th className="border border-gray-300 px-4 py-2">Price</th>
//                   <th className="border border-gray-300 px-4 py-2">Action</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {products.length > 0 ? (
//                   products.map((product) => (
//                     <tr key={product._id} className="hover:bg-gray-100">
//                       <td className="border border-gray-300 px-4 py-2">{product.name}</td>
//                       <td className="border border-gray-300 px-4 py-2">{product.maxquantity}</td>
//                       <td className="border border-gray-300 px-4 py-2">₹{product.price}</td>
//                       <td className="border border-gray-300 px-4 py-2">
//                         <button
//                           onClick={() => setSelectedProduct(product)}
//                           className="bg-blue-500 text-white px-4 py-2 rounded shadow-md hover:bg-blue-600 transition"
//                         >
//                           View
//                         </button>
//                       </td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td
//                       colSpan="4"
//                       className="border border-gray-300 px-4 py-2 text-center text-gray-600"
//                     >
//                       No unapproved products found.
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </div>

//         {/* Sidebar for Product Details */}
//         {selectedProduct && (
//           <div className="fixed right-0 top-0 h-full w-96 bg-gradient-to-r from-white to-green-50 shadow-2xl p-6 overflow-y-auto z-50">
//             <button
//               className="absolute top-4 right-4 text-gray-500 hover:text-black text-2xl"
//               onClick={closeSidebar}
//             >
//               &times;
//             </button>
//             <h2 className="text-3xl font-bold text-green-700 mb-6 border-b pb-2">
//               {selectedProduct.name}
//             </h2>
//             <div className="space-y-6 text-sm">
//               <p>
//                 <span className="font-semibold text-green-600">Description:</span>{" "}
//                 {selectedProduct.description || "No description available"}
//               </p>
//               <p>
//                 <span className="font-semibold text-green-600">Price:</span> ₹{selectedProduct.price}{" "}
//                 <span className="text-sm text-gray-500">({selectedProduct.priceUnit || "per unit"})</span>
//               </p>
//               <p>
//                 <span className="font-semibold text-green-600">Available Stock:</span>{" "}
//                 {selectedProduct.stock || "Out of stock"}
//               </p>
//               <p>
//                 <span className="font-semibold text-green-600">Max Quantity:</span>{" "}
//                 {selectedProduct.maxquantity || "N/A"}
//               </p>
//               <p>
//                 <span className="font-semibold text-green-600">Category:</span>{" "}
//                 {selectedProduct.category?.name || "Uncategorized"}
//               </p>
//               <p className="text-gray-700">
//         <span className="font-semibold text-green-600">Price:</span> ₹{selectedProduct.price} <span className="text-sm text-gray-500">({selectedProduct.priceUnit || "per unit"})</span>
//       </p>
//               <div className="flex justify-end">
//                 <button
//                   onClick={() => handleApproveProduct(selectedProduct._id)}
//                   className="bg-green-500 text-white px-4 py-2 rounded shadow-md hover:bg-green-600 transition"
//                 >
//                   Approve Product
//                 </button>
//               </div>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ApproveProductsPage;
